'use client'

import { useEffect, useState } from 'react'

type ErrorPageResponse = {
  title: string
  description: string | null
  path: string | null
} | null

export default function MarketingError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [page, setPage] = useState<ErrorPageResponse>(null)
  const [isLoadingPage, setIsLoadingPage] = useState(true)

  useEffect(() => {
    const controller = new AbortController()

    const loadErrorPage = async () => {
      try {
        const response = await fetch('/api/default-pages/error', {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal,
        })

        if (!response.ok) return
        const data = (await response.json()) as ErrorPageResponse
        setPage(data)
      } catch {
        // Keep the framework fallback copy if fetching the configured page fails.
      } finally {
        setIsLoadingPage(false)
      }
    }

    void loadErrorPage()

    return () => controller.abort()
  }, [])

  return (
    <main className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-sm uppercase tracking-wide text-muted-foreground">Error</p>
      <h1 className="text-3xl font-semibold">
        {page?.title || 'Something went wrong'}
      </h1>
      <p className="max-w-prose text-muted-foreground">
        {page?.description || 'An unexpected error occurred while loading this page.'}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-md border border-current px-4 py-2 text-sm font-medium transition hover:opacity-80"
        >
          Try again
        </button>
        {page?.path && (
          <a
            href={page.path}
            className="rounded-md border border-current px-4 py-2 text-sm font-medium transition hover:opacity-80"
          >
            Open error page
          </a>
        )}
      </div>
      {!isLoadingPage && error?.digest && (
        <p className="text-xs text-muted-foreground">Ref: {error.digest}</p>
      )}
    </main>
  )
}
